import React, { useEffect, useState } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect,
  useLocation
} from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import ErrorBoundary from './components/ErrorBoundary';
import WelcomePage from './pages/WelcomePage';
import StorePage from './pages/StorePage';
import Footer from './components/Footer';
import theme from './theme';
import { LanguageProvider } from './contexts/LanguageContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ModernLoadingScreen from './components/ModernLoadingScreen';
import LegalDocumentPage from './pages/LegalDocumentPage';
import CookieBanner from './components/CookieBanner';
import SessionExpiredNotification from './components/SessionExpiredNotification';
import sessionManager from './services/sessionManager';
import POIPage from './pages/POIPage';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const ConditionalFooter = () => {
  const location = useLocation();
  const noFooterPaths = ['/welcome', '/operator', '/'];
  const noFooterPrefixes = ['/poi/', '/explore/'];

  const skip =
    noFooterPaths.includes(location.pathname) ||
    noFooterPrefixes.some((prefix) => location.pathname.startsWith(prefix));

  return skip ? null : <Footer />;
};

const AppContent = () => {
  const [showSessionExpired, setShowSessionExpired] = useState(false);

  useEffect(() => {
    const unsubscribe = sessionManager.addListener(() => {
      setShowSessionExpired(true);
    });
    return unsubscribe;
  }, []);

  const handleLoginClick = () => {
    setShowSessionExpired(false);
    sessionManager.clearSessionExpired();
    window.location.href = '/welcome';
  };

  const handleDismissNotification = () => {
    setShowSessionExpired(false);
    sessionManager.clearSessionExpired();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {showSessionExpired && (
        <SessionExpiredNotification
          onLogin={handleLoginClick}
          onDismiss={handleDismissNotification}
        />
      )}
      <div style={{ flex: 1 }}>
        <ScrollToTop />
        <React.Suspense fallback={<ModernLoadingScreen />}>
          <Switch>
            <Route exact path="/welcome" component={WelcomePage} />
            <Route exact path="/store" component={StorePage} />
            <Route exact path="/" render={() => <Redirect to="/welcome" />} />

            <Route path="/login" render={() => <Redirect to="/welcome" />} />
            <Route path="/signup" render={() => <Redirect to="/welcome" />} />
            <Route path="/tours" render={() => <Redirect to="/welcome" />} />
            <Route path="/operators/:id/profile" render={() => <Redirect to="/welcome" />} />
            <Route path="/operator" render={() => <Redirect to="/welcome" />} />
            <Route path="/operator-payment-history" render={() => <Redirect to="/welcome" />} />
            <Route path="/admin" render={() => <Redirect to="/welcome" />} />

            <Route path="/explore/:slug" component={POIPage} />
            <Route path="/poi/:id" component={POIPage} />

            <Route path="/legal/:document" component={LegalDocumentPage} />

            <Route render={() => <Redirect to="/welcome" />} />
          </Switch>
        </React.Suspense>
      </div>
      <ConditionalFooter />
      <ToastContainer />
      <CookieBanner />
    </div>
  );
};

function App() {
  return (
    <LanguageProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ErrorBoundary>
          <Router>
            <AppContent />
          </Router>
        </ErrorBoundary>
      </ThemeProvider>
    </LanguageProvider>
  );
}

export default App;
